import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Layout } from '../components/Layout';
import { useWorkspace } from '../context/WorkspaceContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Badge } from '../components/ui/badge';
import { spaceAPI, workspaceAPI } from '../lib/api';
import { Settings, Users, Shield, Lock, UserCheck, UserX, Crown, Save, Palette, Hash, X, Search, Archive, ArchiveRestore } from 'lucide-react';

const SPACE_COLORS = [
    '#2F6F63', '#3B82F6', '#8B5CF6', '#EC4899', '#EF4444',
    '#F59E0B', '#10B981', '#06B6D4', '#6366F1', '#14B8A6',
    '#F97316', '#84CC16', '#A855F7', '#0EA5E9', '#64748B',
];

export default function SpaceSettingsPage() {
    const { spaceId } = useParams();
    const navigate = useNavigate();
    const { setSpaces } = useWorkspace();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [archiving, setArchiving] = useState(false);
    const [space, setSpace] = useState(null);
    const [workspaceMembers, setWorkspaceMembers] = useState([]);
    const [assignedMembers, setAssignedMembers] = useState([]);
    const [settings, setSettings] = useState({ is_private: false, allowed_member_ids: [], space_admin_ids: [] });

    // General settings draft (name/color/prefix)
    const [general, setGeneral] = useState({ name: '', color: '#2F6F63', prefix: '' });
    const [generalSaving, setGeneralSaving] = useState(false);
    const [allowedMemberSearch, setAllowedMemberSearch] = useState('');

    useEffect(() => { loadSettings(); }, [spaceId]); // eslint-disable-line

    const loadSettings = async () => {
        try {
            setLoading(true);
            const sp = (await spaceAPI.get(spaceId)).data;
            setSpace(sp);
            setGeneral({ name: sp.name || '', color: sp.color || '#2F6F63', prefix: sp.prefix || '' });

            if (sp.workspace_id) {
                const ms = (await workspaceAPI.getMembers(sp.workspace_id)).data;
                setWorkspaceMembers(ms.filter(m => m.role !== 'Client' && m.role !== 'Guest'));
            }
            const stRes = (await spaceAPI.getSpaceSettings(spaceId)).data;
            setSettings(stRes);

            try {
                const am = (await spaceAPI.getAssignedMembers(spaceId)).data;
                setAssignedMembers(am || []);
            } catch (_) { /* non-blocking */ }
        } catch (error) {
            if (error?.response?.status !== 404) toast.error('Gagal memuat pengaturan space');
        } finally { setLoading(false); }
    };

    // ============= General settings (name, color, prefix)
    const saveGeneral = async () => {
        if (!general.name.trim()) return toast.error('Nama wajib diisi');
        const prefixChanged = (general.prefix || '') !== (space.prefix || '');
        if (prefixChanged) {
            const ok = window.confirm(
                `Mengubah prefix akan menulis ulang task code semua task di space ini menjadi "${general.prefix}-N". Lanjutkan?`
            );
            if (!ok) return;
        }
        try {
            setGeneralSaving(true);
            const payload = {
                name: general.name.trim(),
                color: general.color,
                prefix: general.prefix?.trim() || null,
            };
            const res = (await spaceAPI.update(spaceId, payload)).data;
            setSpace(res);
            toast.success(prefixChanged ? 'Space tersimpan, task code di-update.' : 'Space tersimpan.');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Gagal menyimpan space');
        } finally { setGeneralSaving(false); }
    };

    // ============= Privacy
    const handleTogglePrivacy = async (isPrivate) => {
        try {
            setSaving(true);
            const res = await spaceAPI.updateSpaceSettings(spaceId, { is_private: isPrivate });
            // Reload settings to get the auto-populated allowed_member_ids
            const stRes = (await spaceAPI.getSpaceSettings(spaceId)).data;
            setSettings(stRes);
            if (isPrivate) {
                toast.success(`Space dijadikan Private. ${stRes.allowed_member_ids.length} member yang sebelumnya di-assign tetap memiliki akses.`);
            } else {
                toast.success('Space diubah menjadi Public');
            }
            return res;
        } catch (error) { toast.error('Gagal mengubah privacy'); }
        finally { setSaving(false); }
    };

    const handleToggleMember = async (userId, isCurrentlyAllowed) => {
        try {
            if (isCurrentlyAllowed) {
                await spaceAPI.removeMemberFromSpace(spaceId, userId);
                setSettings(prev => ({ ...prev, allowed_member_ids: prev.allowed_member_ids.filter(id => id !== userId) }));
                toast.success('Member dihapus dari space');
            } else {
                await spaceAPI.addMemberToSpace(spaceId, userId);
                setSettings(prev => ({ ...prev, allowed_member_ids: [...prev.allowed_member_ids, userId] }));
                toast.success('Member ditambahkan ke space');
            }
        } catch { toast.error('Gagal mengubah akses member'); }
    };

    const handleAddSpaceAdmin = async (userId) => {
        if (settings.space_admin_ids.includes(userId)) return;
        try {
            await spaceAPI.addSpaceAdmin(spaceId, userId);
            setSettings(prev => ({ ...prev, space_admin_ids: [...prev.space_admin_ids, userId] }));
            toast.success('Member ditunjuk sebagai Admin Space');
        } catch { toast.error('Gagal menambahkan Admin Space'); }
    };
    const handleRemoveSpaceAdmin = async (userId) => {
        try {
            await spaceAPI.removeSpaceAdmin(spaceId, userId);
            setSettings(prev => ({ ...prev, space_admin_ids: prev.space_admin_ids.filter(id => id !== userId) }));
            toast.success('Admin Space role dihapus');
        } catch { toast.error('Gagal menghapus Admin Space'); }
    };

    const handleArchive = async () => {
        if (!window.confirm('Arsipkan space ini? Space akan disembunyikan dari navigasi. Anda bisa memulihkannya dari Settings ▸ Spaces.')) return;
        try {
            setArchiving(true);
            await spaceAPI.archive(spaceId);
            setSpaces(prev => prev.filter(s => s.id !== spaceId));
            toast.success('Space diarsipkan');
            navigate('/');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Gagal mengarsipkan space');
        } finally { setArchiving(false); }
    };

    const handleUnarchive = async () => {
        try {
            setArchiving(true);
            const res = (await spaceAPI.unarchive(spaceId)).data;
            setSpace(res);
            toast.success('Space dipulihkan');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Gagal memulihkan space');
        } finally { setArchiving(false); }
    };

    const adminMembers = useMemo(
        () => workspaceMembers.filter(m => settings.space_admin_ids.includes(m.user_id)),
        [workspaceMembers, settings.space_admin_ids]
    );

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6 max-w-4xl">
                <div className="flex items-center gap-3">
                    <Settings className="w-6 h-6 text-primary" />
                    <h1 className="text-2xl font-bold text-gray-900">
                        Pengaturan Space {space?.name ? `"${space.name}"` : ''}
                    </h1>
                </div>

                {/* General — Name, Color, Prefix */}
                <Card data-testid="space-general-card">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2"><Palette className="w-5 h-5 text-emerald-600" /> Umum</CardTitle>
                        <CardDescription>Nama, warna, dan prefix task code untuk space ini.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-5">
                        <div>
                            <Label>Nama Space</Label>
                            <Input value={general.name} onChange={(e) => setGeneral(g => ({ ...g, name: e.target.value }))} data-testid="space-name-input" />
                        </div>

                        <div>
                            <Label className="mb-2 block">Warna</Label>
                            <div className="flex flex-wrap gap-2">
                                {SPACE_COLORS.map(c => (
                                    <button
                                        type="button"
                                        key={c}
                                        onClick={() => setGeneral(g => ({ ...g, color: c }))}
                                        className={`w-8 h-8 rounded-full border-2 transition ${general.color === c ? 'border-gray-900 scale-110' : 'border-white shadow'}`}
                                        style={{ backgroundColor: c }}
                                        data-testid={`space-color-${c.replace('#', '')}`}
                                        aria-label={`color ${c}`}
                                    />
                                ))}
                            </div>
                        </div>

                        <div>
                            <Label className="flex items-center gap-2"><Hash className="w-4 h-4" /> Prefix Task Code</Label>
                            <Input
                                value={general.prefix}
                                onChange={(e) => setGeneral(g => ({ ...g, prefix: e.target.value.toUpperCase().replace(/\s/g, '') }))}
                                placeholder="MIS. ENG, DSN, OPS"
                                maxLength={10}
                                data-testid="space-prefix-input"
                                className="uppercase"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Format task code: <code className="bg-gray-100 px-1 rounded">{general.prefix || (general.name || 'XXX').slice(0,3).toUpperCase()}-1</code>, <code className="bg-gray-100 px-1 rounded">{general.prefix || (general.name || 'XXX').slice(0,3).toUpperCase()}-2</code>, ...
                                {(general.prefix || '') !== (space?.prefix || '') && (
                                    <span className="block mt-1 text-amber-700">⚠ Saat disimpan, semua task di space ini akan di-renumber.</span>
                                )}
                            </p>
                        </div>

                        <div className="flex justify-end">
                            <Button onClick={saveGeneral} disabled={generalSaving} className="bg-primary text-white" data-testid="space-general-save">
                                <Save className="w-4 h-4 mr-2" /> Simpan Perubahan
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Privacy */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Lock className="w-5 h-5 text-rose-600" /> Privacy
                        </CardTitle>
                        <CardDescription>Atur apakah space ini bisa diakses semua member workspace atau hanya yang di-invite.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                            <div>
                                <div className="font-medium">Private Space</div>
                                <p className="text-sm text-gray-500">Saat di-private, member yang sudah di-assign ke task di space ini otomatis mendapat akses.</p>
                            </div>
                            <Switch
                                checked={settings.is_private}
                                onCheckedChange={handleTogglePrivacy}
                                disabled={saving}
                                data-testid="space-private-toggle"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Allowed members — always visible. Editable only when PUBLIC. */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Users className="w-5 h-5 text-blue-600" />
                            Member Yang Diizinkan ({settings.allowed_member_ids.length})
                            {settings.is_private && (
                                <Badge variant="outline" className="ml-2 text-amber-700 border-amber-400 bg-amber-50">
                                    <Lock className="w-3 h-3 mr-1" /> Read-only
                                </Badge>
                            )}
                        </CardTitle>
                        <CardDescription>
                            {settings.is_private
                                ? 'Space ini Private. List dikelola otomatis oleh menu Team & assignment task. Untuk mengedit manual, ubah ke Public terlebih dahulu.'
                                : 'Space ini Public — semua member workspace bisa akses. List ini akan dipakai sebagai allow-list otomatis bila Anda mengubah space ke Private.'}
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {!settings.is_private && (
                            <AddAllowedMember
                                workspaceMembers={workspaceMembers}
                                allowedIds={settings.allowed_member_ids}
                                onAdd={(uid) => handleToggleMember(uid, false)}
                            />
                        )}

                        {settings.allowed_member_ids.length > 0 && (
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <Input
                                    value={allowedMemberSearch}
                                    onChange={(e) => setAllowedMemberSearch(e.target.value)}
                                    placeholder="Cari member..."
                                    className="pl-9"
                                />
                            </div>
                        )}

                        <div className="space-y-2">
                            {workspaceMembers
                                .filter(m => {
                                    if (!settings.allowed_member_ids.includes(m.user_id)) return false;
                                    if (!allowedMemberSearch) return true;
                                    const q = allowedMemberSearch.toLowerCase();
                                    return (m.user_name || '').toLowerCase().includes(q) || (m.user_email || '').toLowerCase().includes(q);
                                })
                                .map(m => {
                                    const isAdmin = settings.space_admin_ids.includes(m.user_id);
                                    const isAssignedInSpace = assignedMembers.some(a => a.id === m.user_id);
                                    return (
                                        <div key={m.user_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-testid={`space-allowed-row-${m.user_id}`}>
                                            <div className="flex items-center gap-3">
                                                <div className="w-9 h-9 rounded-full bg-primary text-white flex items-center justify-center font-semibold">
                                                    {(m.user_name || '?').charAt(0).toUpperCase()}
                                                </div>
                                                <div>
                                                    <p className="font-medium flex items-center gap-2 flex-wrap">
                                                        {m.user_name}
                                                        {isAdmin && <Badge variant="secondary" className="bg-amber-100 text-amber-800"><Crown className="w-3 h-3 mr-1" />Admin Space</Badge>}
                                                        {isAssignedInSpace && <Badge variant="outline" className="text-blue-700 border-blue-300">assigned di space</Badge>}
                                                    </p>
                                                    <p className="text-xs text-gray-500">{m.user_email}</p>
                                                </div>
                                            </div>
                                            {!settings.is_private && (
                                                <Button onClick={() => handleToggleMember(m.user_id, true)} variant="ghost" size="sm" className="text-red-600">
                                                    <UserX className="w-4 h-4 mr-1" /> Hapus
                                                </Button>
                                            )}
                                        </div>
                                    );
                                })}
                            {settings.allowed_member_ids.length === 0 && (
                                <p className="text-sm text-gray-500 text-center py-6">Belum ada member yang diizinkan.</p>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Space Admins — autocomplete */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Shield className="w-5 h-5 text-purple-600" />
                            Admin Space ({settings.space_admin_ids.length})
                        </CardTitle>
                        <CardDescription>
                            Cari member dan jadikan Admin Space. Admin Space dapat mengelola pengaturan ini.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <AddSpaceAdminCombobox
                            members={workspaceMembers}
                            adminIds={settings.space_admin_ids}
                            onPick={handleAddSpaceAdmin}
                        />

                        <div className="flex flex-wrap gap-2">
                            {adminMembers.length === 0 && (
                                <p className="text-sm text-gray-500">Belum ada Admin Space.</p>
                            )}
                            {adminMembers.map(m => (
                                <div key={m.user_id} className="flex items-center gap-2 pl-3 pr-1 py-1 bg-purple-50 border border-purple-200 rounded-full" data-testid={`space-admin-chip-${m.user_id}`}>
                                    <Crown className="w-3.5 h-3.5 text-amber-600" />
                                    <span className="text-sm font-medium text-purple-900">{m.user_name}</span>
                                    <button onClick={() => handleRemoveSpaceAdmin(m.user_id)} className="ml-1 p-1 rounded-full hover:bg-purple-100" aria-label="remove" data-testid={`space-admin-remove-${m.user_id}`}>
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>

                {/* Archive */}
                <Card data-testid="space-archive-card">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Archive className="w-5 h-5 text-amber-600" /> Arsipkan Space
                        </CardTitle>
                        <CardDescription>
                            Space yang diarsipkan akan disembunyikan dari navigasi. Task &amp; data tidak dihapus dan bisa dipulihkan kapan saja dari Settings ▸ Spaces.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {space?.is_archived ? (
                            <div className="flex items-center justify-between p-3 border border-amber-200 bg-amber-50 rounded-lg">
                                <p className="text-sm text-amber-800">Space ini sedang diarsipkan.</p>
                                <Button onClick={handleUnarchive} disabled={archiving} variant="outline" className="border-emerald-300 text-emerald-700" data-testid="space-unarchive-btn">
                                    <ArchiveRestore className="w-4 h-4 mr-2" /> Pulihkan
                                </Button>
                            </div>
                        ) : (
                            <div className="flex justify-end">
                                <Button onClick={handleArchive} disabled={archiving} variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="space-archive-btn">
                                    <Archive className="w-4 h-4 mr-2" /> Arsipkan Space
                                </Button>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
}

// =============================================================== sub-components
function AddSpaceAdminCombobox({ members, adminIds, onPick }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const candidates = useMemo(() =>
        members.filter(m =>
            !adminIds.includes(m.user_id) &&
            ((m.user_name || '').toLowerCase().includes(query.toLowerCase()) ||
             (m.user_email || '').toLowerCase().includes(query.toLowerCase()))
        ), [members, adminIds, query]);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" className="w-full justify-start" data-testid="space-admin-combobox">
                    <Search className="w-4 h-4 mr-2 text-gray-500" /> Cari member untuk dijadikan Admin Space...
                </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                <Command>
                    <CommandInput placeholder="Ketik nama atau email..." value={query} onValueChange={setQuery} data-testid="space-admin-combobox-input" />
                    <CommandList>
                        <CommandEmpty>Tidak ada member yang cocok.</CommandEmpty>
                        <CommandGroup>
                            {candidates.map(m => (
                                <CommandItem
                                    key={m.user_id}
                                    value={`${m.user_name} ${m.user_email}`}
                                    onSelect={() => { onPick(m.user_id); setOpen(false); setQuery(''); }}
                                    data-testid={`space-admin-option-${m.user_id}`}
                                >
                                    <div className="flex items-center gap-2">
                                        <div className="w-7 h-7 rounded-full bg-primary text-white flex items-center justify-center text-xs font-semibold">
                                            {(m.user_name || '?').charAt(0).toUpperCase()}
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-sm">{m.user_name}</span>
                                            <span className="text-xs text-gray-500">{m.user_email}</span>
                                        </div>
                                    </div>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}

function AddAllowedMember({ workspaceMembers, allowedIds, onAdd }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const candidates = useMemo(() =>
        workspaceMembers.filter(m =>
            !allowedIds.includes(m.user_id) &&
            ((m.user_name || '').toLowerCase().includes(query.toLowerCase()) ||
             (m.user_email || '').toLowerCase().includes(query.toLowerCase()))
        ), [workspaceMembers, allowedIds, query]);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" className="w-full justify-start" data-testid="space-allowed-combobox">
                    <UserCheck className="w-4 h-4 mr-2" /> Tambah member yang diizinkan...
                </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                <Command>
                    <CommandInput placeholder="Ketik nama atau email..." value={query} onValueChange={setQuery} />
                    <CommandList>
                        <CommandEmpty>Tidak ada member yang cocok.</CommandEmpty>
                        <CommandGroup>
                            {candidates.map(m => (
                                <CommandItem
                                    key={m.user_id}
                                    value={`${m.user_name} ${m.user_email}`}
                                    onSelect={() => { onAdd(m.user_id); setOpen(false); setQuery(''); }}
                                    data-testid={`space-allowed-option-${m.user_id}`}
                                >
                                    {m.user_name} <span className="text-xs text-gray-500 ml-2">{m.user_email}</span>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
